import { inject, injectable } from "inversify";
import * as THREE from "three";
import { EngineConfigService, type IService, Logger } from "@designboticteam/core";
import { getParcelsInBbox, MapLibreInstanceService, MapStoreService, type ParcelInfo, POG__getZoneColor, type RingDefinition } from "@designboticteam/maplibre";
import { type IRendererService, RENDERER_SERVICE, SceneService, Workspace3DStoreService } from "@designboticteam/workspace-3d";
import { type ParcelSceneGeometry, ParcelShape, type ParcelZoneSceneGeometry, type ParcelZoneSource } from "./ParcelShape";
import { ParcelSnapLines } from "./ParcelSnapLines";

const PARCEL_SNAP_MIN_ZOOM = 15;
const PARCEL_SNAP_DEBOUNCE_MS = 300;

function toOpenRing(ring: RingDefinition): RingDefinition {
	if (ring.length > 1) {
		const first = ring[0];
		const last = ring[ring.length - 1];

		if (first.lng === last.lng && first.lat === last.lat) {
			return ring.slice(0, -1);
		}
	}

	return ring;
}

@injectable()
class ParcelShapeService implements IService {
	public readonly serviceName = "ParcelShapeService" as const;
	private readonly logger = new Logger(`📐 ${this.serviceName}`, { color: "#ffffff", backgroundColor: "#0891b2" });

	private activeShape: ParcelShape | null = null;

	private readonly chosenShapes = new Map<string, ParcelShape>();
	private storeUnsubscribeFunctions: Array<() => void> = [];

	private readonly snapLines = new ParcelSnapLines();
	private snapLinesTimeout: ReturnType<typeof setTimeout> | null = null;
	private snapLinesRequestId = 0;

	private lastFrameTimeMs = performance.now();

	constructor(
		@inject(MapLibreInstanceService) private readonly mapLibreInstanceService: MapLibreInstanceService,
		@inject(SceneService) private readonly sceneService: SceneService,
		@inject(EngineConfigService) private readonly engineConfigService: EngineConfigService,
		@inject(MapStoreService) private readonly mapStoreService: MapStoreService,
		@inject(RENDERER_SERVICE) private readonly rendererService: IRendererService,
		@inject(Workspace3DStoreService) private readonly workspace3DStoreService: Workspace3DStoreService,
	) {
		this.logger.log("constructed");
	}

	/** */
	public initialize(): void {
		this.storeUnsubscribeFunctions = [
			this.mapStoreService.subscribeToState((state) => state.parcels.activeParcel, this.handleActiveParcelChange, { fireImmediately: true }),
			this.mapStoreService.subscribeToState((state) => state.parcels.selectedParcels, this.handleSelectedParcelsChange, { fireImmediately: true }),
			this.mapStoreService.subscribeToState(
				(state) => state.parcels.isPogSplitVisible,
				(isVisible) => this.activeShape?.setZoneSplitVisible("pog", isVisible),
			),
			this.mapStoreService.subscribeToState(
				(state) => state.parcels.isMpzpSplitVisible,
				(isVisible) => this.activeShape?.setZoneSplitVisible("mpzp", isVisible),
			),
			this.mapStoreService.subscribeToState(
				(state) => state.parcels.isObszarUzupelnieniaZabudowySplitVisible,
				(isVisible) => this.activeShape?.setZoneSplitVisible("obszar_uzupelnienia_zabudowy", isVisible),
			),
			this.mapStoreService.subscribeToState(
				(state) => state.parcels.hoveredSplitZoneIndex,
				(hoveredSplitZoneIndex) => this.activeShape?.setHighlightedZoneIndex(hoveredSplitZoneIndex),
			),
			this.workspace3DStoreService.store.subscribe((state) => state.cursor.isSnappingEnabled, this.handleSnappingEnabledChange, { fireImmediately: true }),
		];

		this.rendererService.addRenderLoopListener(this.animateOutlineDashes);
	}

	/** */
	private handleSnappingEnabledChange = (isSnappingEnabled: boolean): void => {
		const map = this.mapLibreInstanceService.map;

		if (isSnappingEnabled) {
			this.sceneService.scene.add(this.snapLines.sceneObject);
			map.on("moveend", this.scheduleSnapLinesRefresh);
			this.scheduleSnapLinesRefresh();
			return;
		}

		map.off("moveend", this.scheduleSnapLinesRefresh);
		this.cancelSnapLinesRefresh();
		this.snapLines.clear();
		this.snapLines.sceneObject.removeFromParent();
	};

	/** */
	private scheduleSnapLinesRefresh = (): void => {
		this.cancelSnapLinesRefresh();
		this.snapLinesTimeout = setTimeout(() => void this.loadSnapLines(), PARCEL_SNAP_DEBOUNCE_MS);
	};

	/** */
	private cancelSnapLinesRefresh(): void {
		if (this.snapLinesTimeout !== null) {
			clearTimeout(this.snapLinesTimeout);
			this.snapLinesTimeout = null;
		}
	}

	/** */
	private async loadSnapLines(): Promise<void> {
		const requestId = ++this.snapLinesRequestId;
		const map = this.mapLibreInstanceService.map;

		if (map.getZoom() < PARCEL_SNAP_MIN_ZOOM) {
			this.snapLines.clear();
			return;
		}

		const bounds = map.getBounds();

		try {
			const features = await getParcelsInBbox({ minX: bounds.getWest(), minY: bounds.getSouth(), maxX: bounds.getEast(), maxY: bounds.getNorth() });

			if (requestId !== this.snapLinesRequestId) {
				return;
			}

			const rings = features.flatMap((feature) => this.featureOutlinesToScene(feature.geometry)).filter((ring) => ring.length >= 3);
			this.snapLines.setRings(rings);
		} catch (error) {
			this.logger.error(`Failed to load parcels for snapping: ${error}`);
		}
	}

	/** */
	private featureOutlinesToScene(geometry: GeoJSON.Geometry): Array<Array<THREE.Vector3>> {
		const outlines: Array<GeoJSON.Position[]> =
			geometry.type === "Polygon" ? [geometry.coordinates[0]] : geometry.type === "MultiPolygon" ? geometry.coordinates.map((polygon) => polygon[0]) : [];

		return outlines.map((ring) => ring.map(([lng, lat]) => new THREE.Vector3(...this.mapLibreInstanceService.wgs84ToScene([lng, lat]))));
	}

	/** */
	private animateOutlineDashes = (): void => {
		const now = performance.now();
		const deltaSeconds = (now - this.lastFrameTimeMs) / 1000;
		this.lastFrameTimeMs = now;

		this.activeShape?.advanceOutlineDashes({ deltaSeconds, mapZoom: this.mapLibreInstanceService.map.getZoom() });
	};

	/** */
	public dispose(): void {
		this.storeUnsubscribeFunctions.forEach((unsubscribe) => {
			unsubscribe();
		});
		this.storeUnsubscribeFunctions = [];

		this.rendererService.removeRenderLoopListener(this.animateOutlineDashes);

		this.mapLibreInstanceService.map.off("moveend", this.scheduleSnapLinesRefresh);
		this.cancelSnapLinesRefresh();
		this.snapLines.dispose();

		this.clearActiveShape();

		for (const parcelId of [...this.chosenShapes.keys()]) {
			this.removeChosenShape(parcelId);
		}

		this.logger.log("disposed");
	}

	/** */
	private projectParcelToScene(parcel: ParcelInfo): ParcelSceneGeometry {
		const projectRing = (ring: RingDefinition): Array<THREE.Vector3> =>
			toOpenRing(ring).map((point) => new THREE.Vector3(...this.mapLibreInstanceService.wgs84ToScene(point)));

		const toSceneRing = (parts: ParcelInfo["polygons"]): Array<Array<THREE.Vector3>> =>
			parts.map((part) => projectRing(part.outline)).filter((ring) => ring.length >= 3);

		// The data pipeline nulls a zone's geometry when it covers ~the whole parcel, so fall back to the outline.
		const toZoneGeometry = (zonePolygons: ParcelInfo["polygons"], color: string, symbol: string, source: ParcelZoneSource): ParcelZoneSceneGeometry => ({
			color,
			symbol,
			source,
			rings: toSceneRing(zonePolygons.length > 0 ? zonePolygons : parcel.polygons),
		});

		const palette = this.engineConfigService.palette;

		return {
			outline: toSceneRing(parcel.polygons),
			zones: [
				...parcel.pog_zones.map((zone) => toZoneGeometry(zone.polygons, POG__getZoneColor(zone.symbol), zone.symbol, "pog")),
				...parcel.mpzp_zones.map((zone) => toZoneGeometry(zone.polygons, zone.kolor === "" ? palette.neutral.graphite : zone.kolor, zone.symbol, "mpzp")),
				...parcel.obszar_uzupelnienia_zabudowy_zones.map((zone) =>
					toZoneGeometry(zone.polygons, palette.main.secondary, zone.oznaczenie, "obszar_uzupelnienia_zabudowy"),
				),
			],
		};
	}

	/**
	 * subscribed to: state.parcels.activeParcel
	 */
	private handleActiveParcelChange = (activeParcel: ParcelInfo | null) => {
		this.clearActiveShape();

		if (activeParcel === null) {
			return;
		}

		const shape = new ParcelShape({
			geometry: this.projectParcelToScene(activeParcel),
			palette: this.engineConfigService.palette,
		});

		this.activeShape = shape;
		this.sceneService.scene.add(shape.sceneObject);

		// A new active parcel inherits the zone-split toggles, so switching parcels keeps the split shown.
		const { isPogSplitVisible, isMpzpSplitVisible } = this.mapStoreService.state.parcels;
		shape.setZoneSplitVisible("pog", isPogSplitVisible);
		shape.setZoneSplitVisible("mpzp", isMpzpSplitVisible);

		const element = document.createElement("div");
		Object.assign(element.style, {
			position: "relative",
			width: "0",
			height: "0",
		});
	};

	/** */
	private handleSelectedParcelsChange = (selectedParcels: Array<ParcelInfo>) => {
		const selectedParcelIds = new Set(selectedParcels.map((parcel) => parcel.id));

		for (const parcelId of [...this.chosenShapes.keys()]) {
			if (selectedParcelIds.has(parcelId) === false) {
				this.removeChosenShape(parcelId);
			}
		}

		for (const parcel of selectedParcels) {
			if (this.chosenShapes.has(parcel.id) === false) {
				this.addChosenShape(parcel);
			}
		}
	};

	/** */
	private addChosenShape(parcel: ParcelInfo): void {
		const shape = new ParcelShape({
			geometry: this.projectParcelToScene(parcel),
			palette: this.engineConfigService.palette,
			showZones: false,
			outlineColor: this.engineConfigService.palette.accent.primary,
			isOutlineDashed: false,
		});

		this.sceneService.scene.add(shape.sceneObject);
		this.chosenShapes.set(parcel.id, shape);
	}

	/** */
	private removeChosenShape(parcelId: string): void {
		const shape = this.chosenShapes.get(parcelId);

		if (shape === undefined) {
			return;
		}

		shape.sceneObject.removeFromParent();
		shape.dispose();
		this.chosenShapes.delete(parcelId);
	}

	/** */
	private clearActiveShape(): void {
		if (this.activeShape === null) {
			return;
		}

		this.activeShape.sceneObject.removeFromParent();
		this.activeShape.dispose();
		this.activeShape = null;
	}
}

export { ParcelShapeService };
